import { getEnchantmentName } from "./enchantmentNames";
import type { ItemMatchRuleDto, ItemMatchRuleRequest, SellbotDimensionDto } from "../api/generated/schemas";

// The item-identity vocabulary both owners of ItemMatchRule speak: sellbot offers and AH watches.
// Mirrors Remotex.Core.Models.ItemMatchRuleType / ItemMatchOperator.
export const NUMERIC_OPS = ["Lt", "Le", "Eq", "Ge", "Gt"] as const;

export const RULE_TYPES = [
  "DisplayName",
  "Lore",
  "Enchantment",
  "NbtContains",
  "DurabilityRemaining",
  "Metadata",
  "RepairCost",
  "NbtTagCount",
  "Signature",
  "SkullOwner",
] as const;

export function opsFor(type: string): readonly string[] {
  if (type === "DisplayName") return ["Eq", "Contains"];
  if (type === "Lore") return ["Contains", "Eq"];
  if (type === "NbtContains") return ["Exists", "Eq"];
  if (type === "Enchantment") return ["Exists", ...NUMERIC_OPS];
  if (type === "Signature") return ["Eq", "Contains", "Exists"];
  if (type === "SkullOwner") return ["Eq", "Exists"];
  return NUMERIC_OPS;
}

// Mirrors SellbotOfferDimensions.KeyOf on the server: the one dimension a rule governs.
export function dimensionKey(kind: string, index: number, path: string | null): string {
  return `${kind}/${index}/${path ?? ""}`;
}

export function dimensionOf(rule: { ruleType: string; numericValue: number; stringValue?: string | null }): string {
  const kind = rule.ruleType === "DurabilityRemaining" ? "Metadata" : rule.ruleType;
  if (kind === "Lore") return dimensionKey(kind, rule.numericValue, null);
  if (kind === "Enchantment" || kind === "NbtContains") return dimensionKey(kind, 0, rule.stringValue ?? null);
  return dimensionKey(kind, 0, null);
}

export function dimensionLabel(kind: string, index: number, path: string | null,
  t: (key: string, ...args: (string | number)[]) => string): string {
  if (kind === "Lore") return `${t("SELLBOT_RULE_TYPE_Lore")} ${index + 1}`;
  if (kind === "Enchantment") return getEnchantmentName(Number(path));
  if (kind === "NbtContains") return path ?? "";
  return t("SELLBOT_RULE_TYPE_" + kind);
}

export function dimensionLabelOf(dimension: Pick<SellbotDimensionDto, "kind" | "index" | "path">,
  t: (key: string, ...args: (string | number)[]) => string): string {
  return dimensionLabel(dimension.kind, dimension.index, dimension.path ?? null, t);
}

export function sameRule(left: ItemMatchRuleRequest, right: ItemMatchRuleRequest): boolean {
  return left.ruleType === right.ruleType
    && left.operator === right.operator
    && left.numericValue === right.numericValue
    && (left.stringValue ?? "") === (right.stringValue ?? "")
    && (left.stringValue2 ?? "") === (right.stringValue2 ?? "")
    && (left.stringValue3 ?? "") === (right.stringValue3 ?? "")
    && left.stripColorCodes === right.stripColorCodes
    && left.isEnabled === right.isEnabled;
}

export function asRequest(rule: ItemMatchRuleDto): ItemMatchRuleRequest {
  return {
    ruleType: rule.ruleType, operator: rule.operator, numericValue: rule.numericValue,
    stringValue: rule.stringValue ?? null, stringValue2: rule.stringValue2 ?? null,
    stringValue3: rule.stringValue3 ?? null,
    stripColorCodes: rule.stripColorCodes, isEnabled: rule.isEnabled,
  };
}

// Both compare the stack's damage field: it is wear on a damageable item, and the variant on every
// other one, where a spawn egg with damage 66 is a witch egg.
export function comparesDamageField(type: string): boolean {
  return type === "DurabilityRemaining" || type === "Metadata";
}

export function takesStringValue(type: string): boolean {
  return type === "DisplayName" || type === "Lore" || type === "NbtContains"
    || type === "Enchantment" || type === "Signature" || type === "SkullOwner";
}

export function takesNumericValue(type: string): boolean {
  return comparesDamageField(type) || type === "RepairCost" || type === "NbtTagCount"
    || type === "Lore" || type === "Enchantment";
}

export function defaultOperatorFor(type: string): string {
  if (type === "Lore") return "Contains";
  if (type === "NbtContains") return "Exists";
  if (type === "Enchantment") return "Ge";
  return "Eq";
}

export function ruleTypeIcon(type: string): string {
  if (type === "DurabilityRemaining") return "fa-heart-crack";
  if (type === "Metadata") return "fa-hashtag";
  if (type === "RepairCost") return "fa-hammer";
  if (type === "NbtTagCount") return "fa-tags";
  if (type === "DisplayName") return "fa-font";
  if (type === "Enchantment") return "fa-wand-sparkles";
  if (type === "NbtContains") return "fa-code";
  if (type === "Signature") return "fa-signature";
  if (type === "SkullOwner") return "fa-user";
  return "fa-align-left";
}

export function blankRule(ruleType: string, operator: string): ItemMatchRuleRequest {
  return {
    ruleType,
    operator,
    numericValue: 0,
    stringValue: null,
    stringValue2: null,
    stringValue3: null,
    stripColorCodes: false,
    isEnabled: true,
  };
}
